import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Slider from 'react-slick';
import IconConfigWithTheme from 'components/common/icon-config-with-theme';
import { ServiceRelateBox, ServiceRelateStyle } from './Landing.styled';
import { useTranslation } from 'react-i18next';
import { useThemeWithPartner } from 'components/common/ThemeConfigProvider';

// Define types for the service configuration
interface ServiceConfig {
    desktop: string;
    mobile: string;
    path: string;
    alt: string;
}
type ServiceDataList = {
    path: string;
    titleIcon: {
        type: 'icon' | 'image';
        pageName?: string;
        iconName?: string;
        imageOptions?: React.ImgHTMLAttributes<HTMLImageElement>;
    };
    title: string;
    detail: React.ReactNode;
    subDetail: React.ReactNode;
    footer: string;
    imgUrl: string;
    backgroundColor: string;
    alt: string;
};
const serviceListConfig: Record<string, ServiceConfig> = {
    telepharmacy: {
        desktop: '/images/service-landing/service-card/desktop/telepharmacy-desktop.webp',
        mobile: '/images/service-landing/service-card/mobile/telepharmacy-mobile.webp',
        path: '/service/telepharmacy',
        alt: '',
    },
    telepharmacyHumatrix: {
        desktop: '/images/service-landing/service-card/desktop/telepharmacy-desktop.webp',
        mobile: '/images/service-landing/service-card/mobile/telepharmacy-mobile.webp',
        path: '/service/telepharmacy?voucher=TLP&billing=human01',
        alt: '',
    },
    consult: {
        desktop: '/images/service-landing/service-card/desktop/consult-desktop.webp',
        mobile: '/images/service-landing/service-card/mobile/consult-mobile.webp',
        path: '',
        alt: '',
    },
    risk: {
        desktop: '/images/service-landing/service-card/desktop/risk-desktop.webp',
        mobile: '/images/service-landing/service-card/mobile/risk-mobile.webp',
        path: '',
        alt: '',
    },
    telemed: {
        desktop: '/images/service-landing/service-card/desktop/telemed-desktop.webp',
        mobile: '/images/service-landing/service-card/mobile/telemed-mobile.webp',
        path: '/service/telemed',
        alt: 'ปรึกษาแพทย์ออนไลน์',
    },
    atk: {
        desktop: '/images/service-landing/service-card/desktop/atk-desktop.webp',
        mobile: '/images/service-landing/service-card/mobile/atk-mobile.webp',
        path: '',
        alt: '',
    },
    health: {
        desktop: '/images/service-landing/service-card/desktop/health-desktop.webp',
        mobile: '/images/service-landing/service-card/mobile/health-mobile.webp',
        path: '',
        alt: '',
    },
    nutritionist: {
        desktop: '/images/service-landing/service-card/desktop/nutritionist-desktop.webp',
        mobile: '/images/service-landing/service-card/mobile/nutritionist-mobile.webp',
        path: '',
        alt: '',
    },
    physiotherapy: {
        desktop: '/images/service-landing/service-card/desktop/physiotherapy-desktop.webp',
        mobile: '/images/service-landing/service-card/mobile/physiotherapy-mobile.webp',
        path: '',
        alt: '',
    },
    storeNearby: {
        desktop: '/images/service-landing/service-card/desktop/store-desktop.webp',
        mobile: '/images/service-landing/service-card/mobile/store-mobile.webp',
        path: '/store/nearby?filter=true&type=WALK_IN*GATE_WAY*VIDEO_CALL',
        alt: 'ร้านขายยาใกล้ฉัน',
    },
};

const getSliderConfig = (serviceCount: number) => ({
    arrows: false,
    dots: false,
    infinite: serviceCount > 4,
    slidesToShow: 4,
    slidesToScroll: 1,
    centerMode: true,
    responsive: [
        { breakpoint: 1400, settings: { slidesToShow: 3, infinite: serviceCount > 3 } },
        { breakpoint: 1200, settings: { slidesToShow: 2, infinite: serviceCount > 2 } },
        { breakpoint: 992, settings: { slidesToShow: 4, infinite: serviceCount > 4 } },
        { breakpoint: 768, settings: { slidesToShow: 3, infinite: serviceCount > 3 } },
        { breakpoint: 576, settings: { slidesToShow: 2, infinite: serviceCount > 2 } },
        { breakpoint: 374, settings: { slidesToShow: 1, infinite: serviceCount > 1 } },
    ],
});
const serviceSlideSetting = {
    arrows: false,
    dots: true,
    infinite: false,
    speed: 500,
    slidesToShow: 4,
    slidesToScroll: 1,
    responsive: [
        {
            breakpoint: 1680,
            settings: {
                slidesToShow: 3,
                slidesToScroll: 1,
                infinite: false,
                dots: true,
            },
        },
        {
            breakpoint: 1200,
            settings: {
                slidesToShow: 2,
                slidesToScroll: 1,
                infinite: false,
                dots: true,
            },
        },
        {
            breakpoint: 1080,
            settings: {
                slidesToShow: 2,
                slidesToScroll: 1,
                infinite: false,
                dots: true,
            },
        },
        {
            breakpoint: 767,
            settings: {
                slidesToShow: 1,
                slidesToScroll: 1,
                infinite: false,
                centerMode: true,
                dots: true,
            },
        },
    ],
};

export default function ServiceRelate({ serviceList = [] }: Readonly<{ serviceList: string[] }>) {
    const navigate = useNavigate();
    const sliderConfig = useMemo(() => getSliderConfig(serviceList.length), [serviceList.length]);
    const { t } = useTranslation(['home']);
    const theme = useThemeWithPartner();

    const serviceCardData: { [serviceName: string]: ServiceDataList } = {
        telemed: {
            titleIcon: { type: 'icon', pageName: 'home_service', iconName: 'tlm' },
            title: t('home:telemedicine'),
            detail: t('home:book_a_doctor_s_consultation'),
            subDetail: '',
            footer: t('home:book_now'),
            imgUrl: '/images/home/home-service-telemed.png',
            backgroundColor: '#3a94ff',
            path: '/service/telemed',
            alt: 'ปรึกษาแพทย์ออนไลน์',
        },

        physiotherapy: {
            titleIcon: { type: 'icon', pageName: 'home_service', iconName: 'pt' },
            title: t('home:physiotherapy'),
            detail: 'ปวดเมื่อยกล้ามเนื้อ ต้องการปรึกษานักกายภาพบำบัด',
            subDetail: '',
            footer: t('home:physiotherapy'),
            imgUrl: '/images/home/home-service-pt.png',
            backgroundColor: '#4f9ecb',
            path: '',
            alt: '',
        },
        nutritionist: {
            titleIcon: { type: 'icon', pageName: 'home_service', iconName: 'nt' },
            title: t('home:nutritionist'),
            detail: 'ตอบข้อสงสัยเกี่ยวกับอาหารการกิน อาหารลดน้ำหนัก อาหารเบาหวาน หรือโรคอื่นๆ ที่ต้องควบคุมอาหาร',
            subDetail: '',
            footer: t('home:nutritionist'),
            imgUrl: '/images/home/home-service-nt.png',
            backgroundColor: '#3ba274',
            path: '',
            alt: '',
        },

        telepharmacy: {
            titleIcon: { type: 'icon', pageName: 'home_service', iconName: 'telepharmacy' },
            title: t('home:telepharmacy'),
            detail:
                theme.name !== 'default'
                    ? t('home:get_instant_consultations_tlp')
                    : t('home:get_instant_consultations') + t('home:like_having_a_family_pharmacist'),
            subDetail: (
                <>
                    {t('home:consultation_fees_may_be_applicable')}
                    <br />
                    {t('home:or_use_voucher_code')}
                </>
            ),
            footer: t('home:consult_now'),
            imgUrl: '/images/home/home-service-tlp.png',
            backgroundColor: 'var(--orange-3)',
            path: '/service/telepharmacy',
            alt: '',
        },
        telepharmacyHumatrix: {
            titleIcon: { type: 'icon', pageName: 'home_service', iconName: 'telepharmacy' },
            title: t('home:telepharmacy'),
            detail:
                theme.name !== 'default'
                    ? t('home:get_instant_consultations_tlp')
                    : t('home:get_instant_consultations') + t('home:like_having_a_family_pharmacist'),
            subDetail: (
                <>
                    {t('home:consultation_fees_may_be_applicable')}
                    <br />
                    {t('home:or_use_voucher_code')}
                </>
            ),
            footer: t('home:consult_now'),
            imgUrl: '/images/home/home-service-tlp.png',
            backgroundColor: 'var(--orange-3)',
            path: '/service/telepharmacy?voucher=TLP&billing=human01',
            alt: '',
        },

        storeNearby: {
            titleIcon: { type: 'icon', pageName: 'home_service', iconName: 'nearby' },
            title: t('home:pharmacy_directory'),
            detail:
                theme.name !== 'default'
                    ? t('home:find_network_pharmacies_near_you')
                    : t('home:find_quality_pharmcare_network_pharmacies_near_you'),
            subDetail: '',
            footer: t('home:find_a_pharmacy'),
            imgUrl: '/images/home/home-serivcr-nearby.png',
            backgroundColor: 'var(--blue-3)',
            path: '/store/nearby?filter=true&type=WALK_IN*GATE_WAY*VIDEO_CALL',
            alt: 'ร้านขายยาใกล้ฉัน',
        },
        atk: {
            titleIcon: (
                <img
                    src='/images/home/home-service-atk.svg'
                    className='d-inline-block'
                    style={{ width: '1.1rem', height: '1.1rem' }}
                    alt=''
                />
            ),
            title: t('home:purchase_covid_19_antigen_test_kit'),
            detail: (
                <>
                    {t('home:worried_you_may_be_at_risk')}
                    <br />
                    {t('home:consult_a_pharmacist_to_get_your_antigen_test_kit')}
                </>
            ),
            subDetail: '',
            footer: '',
            imgUrl: '/images/home/service-covid.png',
            backgroundColor: '#b57ba6',
            path: '',
            alt: '',
        },
        risk: {
            titleIcon: { type: 'icon', pageName: 'home_service', iconName: 'telepharmacyLTC' },
            title: 'คัดกรองความเสี่ยง',
            detail: 'ตรวจประเมิน และคัดกรองโรคเพื่อให้ได้รับ การวินิจฉัน และรับการรักษาต่อไป',
            subDetail: '',
            footer: '',
            imgUrl: '/images/service-landing/risk.jpg',
            backgroundColor: '#b57ba6',
            path: '',
            alt: '',
        },
    };
    // Memoized rendering of services
    const renderedServices = useMemo(() => {
        return serviceList.map((serviceKey, index) => {
            const service = serviceListConfig[serviceKey];
            if (!service) return null; // Guard clause for missing service

            // Special handling for storeNearby
            const servicePath =
                window.location.origin.includes('fastcare.co') && serviceKey === 'storeNearby'
                    ? '/store/nearby?filter=true&type=WALK_IN*GATE_WAY*VIDEO_CALL&voucher=fastcare-storenearby'
                    : service.path;

            return (
                <div key={index} onClick={() => navigate(servicePath + '#')}>
                    <img src={service.desktop} alt={service.alt} title={service.alt} className='slide-desktop' />
                    <img src={service.mobile} alt={service.alt} title={service.alt} className='slide-mobile' />
                </div>
            );
        });
    }, [serviceList, navigate]);

    const renderedServices2 = useMemo(() => {
        return serviceList.map((serviceKey, index) => {
            const service = serviceCardData[serviceKey];
            if (!service) return null; // Guard clause for missing service

            // Special handling for storeNearby
            const servicePath =
                window.location.origin.includes('fastcare.co') && serviceKey === 'storeNearby'
                    ? '/store/nearby?filter=true&type=WALK_IN*GATE_WAY*VIDEO_CALL&voucher=fastcare-storenearby'
                    : service.path;

            return (
                <div className='h-100' key={index}>
                    <ServiceRelateBox backgroundColor={service.backgroundColor}>
                        <a href='#' className='service-box-container h-100' onClick={() => navigate(servicePath + '#')}>
                            <div className='service-box-title' data-test-id='title'>
                                {service.titleIcon.type === 'icon' ? (
                                    <IconConfigWithTheme
                                        pageName={service.titleIcon.pageName as string}
                                        iconName={service.titleIcon.iconName as string}
                                    />
                                ) : (
                                    <img {...service.titleIcon.imageOptions} />
                                )}{' '}
                                {service.title}
                            </div>
                            <div className='service-box-content'>
                                <div className='service-box-detail'>
                                    {service.detail}
                                    <div className='-text-subDetail'>{service.subDetail}</div>
                                </div>
                                <div className='service-box-img'>
                                    <img loading='eager' src={service.imgUrl} alt={service.alt} title={service.alt} />
                                </div>
                            </div>
                            {service.footer && (
                                <div className='service-box-goto'>
                                    <span>{service.footer}</span>&nbsp;
                                </div>
                            )}
                        </a>
                    </ServiceRelateBox>
                </div>
            );
        });
    }, [serviceList, navigate]);
    return (
        <ServiceRelateStyle>
            <div className='landing-panel'>
                <h2 className='title'>บริการที่เกี่ยวข้อง</h2>
                <div className='landing-content'>
                    <div className='service-slide'>
                        {/* <Slider {...sliderConfig}>
                            {renderedServices}
                        </Slider> */}
                        <Slider {...serviceSlideSetting}>{renderedServices2}</Slider>
                    </div>
                    <div className='all-service' onClick={() => navigate('/')}>
                        <span>ดูบริการทั้งหมด</span>
                        <IconConfigWithTheme pageName='service_relate' iconName='see_more' />
                    </div>
                </div>
            </div>
        </ServiceRelateStyle>
    );
}
